import React, { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Logout as LogoutIcon, Settings as SettingsIcon } from '@mui/icons-material';

import {
  Avatar,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  IconButton,
} from '@mui/material';
import { Box } from '@mui/system';

import useAuth from '../../hooks/useAuth';
import { isUserRole, ROLES } from '../../utils/jwt';

const SETTINGS_PATHS_BY_ROLE = {
  [ROLES.ADMIN]: '/admin/settings',
  [ROLES.SALESPERSON]: '/salesperson/settings',
  [ROLES.PRODUCTION]: '/produktion/settings',
  [ROLES.CUSTOMER]: '/client/settings',
  [ROLES.PARTNER]: '/partner/settings',
  [ROLES.USER]: '/user/settings',
};

const SETTINGS_PATHS_BY_SEGMENT = {
  admin: '/admin/settings',
  salesperson: '/salesperson/settings',
  produktion: '/produktion/settings',
  client: '/client/settings',
  partner: '/partner/settings',
  user: '/user/settings',
};

const getSettingsPath = (pathname) => {
  const activeSegment = pathname.split('/').filter(Boolean)[0];
  if (SETTINGS_PATHS_BY_SEGMENT[activeSegment]) {
    return SETTINGS_PATHS_BY_SEGMENT[activeSegment];
  }
  return SETTINGS_PATHS_BY_ROLE[isUserRole()] || '/user/settings';
};

const getDisplayName = (user) =>
  [user?.firstName, user?.lastName].filter(Boolean).join(' ') ||
  user?.name ||
  user?.email ||
  'Användare';

const getInitials = (displayName) =>
  displayName
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase())
    .slice(0, 2)
    .join('') || '?';

const NavbarUserDropdown = () => {
  const [anchorMenu, setAnchorMenu] = React.useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { signOut, setUserMe, user } = useAuth();
  const displayName = useMemo(() => getDisplayName(user), [user]);
  const initials = useMemo(() => getInitials(displayName), [displayName]);
  const settingsPath = useMemo(() => getSettingsPath(location.pathname), [location.pathname]);

  const toggleMenu = (event) => {
    setAnchorMenu(event.currentTarget);
  };

  const closeMenu = () => {
    setAnchorMenu(null);
  };

  const handleSettings = () => {
    closeMenu();
    navigate(settingsPath);
  };

  const handleSignOut = async () => {
    closeMenu();
    await signOut();
    setUserMe(null);
    navigate('/auth/sign-in');
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'right',
        color: '#646363',
      }}>
      <Tooltip title={displayName}>
        <IconButton
          aria-controls={anchorMenu ? 'menu-appbar' : undefined}
          aria-haspopup="true"
          aria-expanded={anchorMenu ? 'true' : undefined}
          onClick={toggleMenu}
          color="inherit"
          size="large"
          sx={{
            p: { xs: 0.75, sm: 1 },
          }}
        >
          <Avatar
            sx={{
              width: 34,
              height: 34,
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              fontSize: 14,
              fontWeight: 600,
            }}
          >
            {initials}
          </Avatar>
        </IconButton>
      </Tooltip>
      <Menu
        id="menu-appbar"
        anchorEl={anchorMenu}
        open={Boolean(anchorMenu)}
        onClose={closeMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            minWidth: 180,
            mt: 1,
          },
        }}
      >
        <MenuItem
          onClick={handleSettings}
          sx={{
            fontSize: '14px',
            padding: '10px 16px',
          }}
        >
          <ListItemIcon>
            <SettingsIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Inställningar" />
        </MenuItem>
        <MenuItem
          onClick={handleSignOut}
          sx={{
            fontSize: '14px',
            padding: '10px 16px',
          }}
        >
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Logga ut" />
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default NavbarUserDropdown;
